<template>
  <div class="doc-detail">
    <a-page-header
      :title="doc?.title || '文档详情'"
      @back="goBack"
    >
      <template #extra>
        <a-space>
          <a-tooltip title="上一篇" v-if="prevDoc">
            <a-button @click="goToDoc(prevDoc.file)">
              <template #icon><LeftOutlined /></template>
            </a-button>
          </a-tooltip>
          <a-tooltip title="下一篇" v-if="nextDoc">
            <a-button @click="goToDoc(nextDoc.file)">
              <template #icon><RightOutlined /></template>
            </a-button>
          </a-tooltip>
        </a-space>
      </template>
      <template #footer>
        <a-breadcrumb>
          <a-breadcrumb-item>
            <router-link to="/docs">系统文档</router-link>
          </a-breadcrumb-item>
          <a-breadcrumb-item>
            <router-link :to="`/docs/${category}`">{{ categoryName }}</router-link>
          </a-breadcrumb-item>
          <a-breadcrumb-item>{{ doc?.title }}</a-breadcrumb-item>
        </a-breadcrumb>
      </template>
    </a-page-header>

    <a-spin :spinning="loading">
      <div class="doc-wrapper">
        <!-- 文档目录 -->
        <div class="doc-toc" v-if="toc.length > 0">
          <a-card size="small" title="目录" :bordered="false">
            <a-anchor :affix="false" :offsetTop="100">
              <a-anchor-link
                v-for="item in toc"
                :key="item.id"
                :href="`#${item.id}`"
                :title="item.text"
                :class="[`toc-level-${item.level}`]"
              />
            </a-anchor>
          </a-card>
        </div>

        <!-- 文档内容 -->
        <div class="doc-content">
          <a-card :bordered="false">
            <div 
              class="markdown-body" 
              v-html="renderedContent"
            ></div>
            
            <a-divider />
            
            <div class="doc-meta">
              <span v-if="doc?.lastModified">
                <ClockCircleOutlined /> 
                最后更新: {{ formatDate(doc.lastModified) }}
              </span>
              <span v-if="doc?.size">
                <FileTextOutlined style="margin-left: 16px" /> 
                文件大小: {{ formatSize(doc.size) }}
              </span>
            </div>
          </a-card>
        </div>
      </div>
    </a-spin>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import request from '@/api/request';
import { marked } from 'marked';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';
import {
  LeftOutlined,
  RightOutlined,
  ClockCircleOutlined,
  FileTextOutlined
} from '@ant-design/icons-vue';

export default {
  name: 'DocsDetail',
  components: {
    LeftOutlined,
    RightOutlined,
    ClockCircleOutlined,
    FileTextOutlined
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const category = ref('');
    const filename = ref('');
    const doc = ref(null);
    const categoryName = ref('');
    const categoryDocs = ref([]);
    const loading = ref(false);
    const toc = ref([]);

    // 配置 marked
    marked.setOptions({
      gfm: true,
      breaks: true,
    });

    // 渲染后的内容
    const renderedContent = computed(() => {
      if (!doc.value?.content) return '';
      
      // 生成带 id 的标题
      const renderer = new marked.Renderer();
      const headings = [];
      
      // 新版 marked 的 heading 参数格式: { text, depth, raw }
      renderer.heading = ({ text, depth }) => {
        const textStr = String(text || '');
        const id = textStr.toLowerCase()
          .replace(/[^\w\u4e00-\u9fa5]+/g, '-')
          .replace(/^-+|-+$/g, '');
        
        headings.push({ id, text: textStr, level: depth });
        
        return `<h${depth} id="${id}">${textStr}</h${depth}>`;
      };
      
      // 代码高亮
      renderer.code = ({ text, lang }) => {
        const code = String(text || '');
        let highlighted;
        if (lang && hljs.getLanguage(lang)) {
          try {
            highlighted = hljs.highlight(code, { language: lang }).value;
          } catch (e) {
            highlighted = code;
          }
        } else {
          highlighted = hljs.highlightAuto(code).value;
        }
        return `<pre><code class="hljs language-${lang || ''}">${highlighted}</code></pre>`;
      };

      const html = marked(doc.value.content, { renderer });
      
      // 更新目录
      nextTick(() => {
        toc.value = headings.filter(h => h.level <= 3);
      });
      
      return html;
    });

    // 上一篇/下一篇
    const currentIndex = computed(() => {
      return categoryDocs.value.findIndex(d => d.file === filename.value);
    });

    const prevDoc = computed(() => {
      if (currentIndex.value > 0) {
        return categoryDocs.value[currentIndex.value - 1];
      }
      return null;
    });

    const nextDoc = computed(() => {
      if (currentIndex.value < categoryDocs.value.length - 1) {
        return categoryDocs.value[currentIndex.value + 1];
      }
      return null;
    });

    // 加载文档
    const loadDoc = async () => {
      if (!category.value || !filename.value) return;
      
      loading.value = true;
      try {
        // 并行加载文档内容和分类信息
        const [docRes, categoryRes] = await Promise.all([
          request.get(`/docs/content/${category.value}/${filename.value}`),
          request.get(`/docs/category/${category.value}`)
        ]);
        
        if (docRes.code === 200) {
          doc.value = docRes.data;
        }
        
        if (categoryRes.code === 200) {
          categoryName.value = categoryRes.data.name;
          categoryDocs.value = categoryRes.data.docs || [];
        }
      } catch (error) {
        console.error('加载文档失败:', error);
      } finally {
        loading.value = false;
      }
    };

    // 格式化日期
    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // 格式化文件大小
    const formatSize = (bytes) => {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1024 / 1024).toFixed(1) + ' MB';
    };

    // 跳转到文档
    const goToDoc = (file) => {
      router.push(`/docs/${category.value}/${file}`);
    };

    // 返回
    const goBack = () => {
      router.push(`/docs/${category.value}`);
    };

    // 监听路由变化
    watch(() => [route.params.category, route.params.filename], ([newCategory, newFilename]) => {
      if (newCategory && newFilename) {
        category.value = newCategory;
        filename.value = newFilename;
        loadDoc();
      }
    }, { immediate: true });

    onMounted(() => {
      category.value = route.params.category;
      filename.value = route.params.filename;
      loadDoc();
    });

    return {
      category,
      filename,
      doc,
      categoryName,
      loading,
      renderedContent,
      toc,
      prevDoc,
      nextDoc,
      formatDate,
      formatSize,
      goToDoc,
      goBack,
    };
  }
};
</script>

<style scoped>
.doc-detail {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.doc-wrapper {
  display: flex;
  gap: 24px;
  margin-top: 16px;
}

.doc-toc {
  width: 240px;
  flex-shrink: 0;
}

.doc-toc :deep(.ant-anchor) {
  padding-left: 0;
}

.doc-toc :deep(.toc-level-2) {
  padding-left: 12px;
}

.doc-toc :deep(.toc-level-3) {
  padding-left: 24px;
  font-size: 12px;
}

.doc-content {
  flex: 1;
  min-width: 0;
}

.doc-meta {
  color: #999;
  font-size: 13px;
}

/* Markdown 样式 */
.markdown-body {
  font-size: 15px;
  line-height: 1.8;
  color: #333;
}

.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3),
.markdown-body :deep(h4) {
  margin-top: 24px;
  margin-bottom: 16px;
  font-weight: 600;
  line-height: 1.4;
}

.markdown-body :deep(h1) {
  font-size: 28px;
  border-bottom: 1px solid #eee;
  padding-bottom: 12px;
}

.markdown-body :deep(h2) {
  font-size: 22px;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.markdown-body :deep(h3) {
  font-size: 18px;
}

.markdown-body :deep(p) {
  margin-bottom: 16px;
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  padding-left: 24px;
  margin-bottom: 16px;
}

.markdown-body :deep(li) {
  margin-bottom: 8px;
}

.markdown-body :deep(pre) {
  background-color: #f6f8fa;
  border-radius: 6px;
  padding: 16px;
  overflow-x: auto;
  margin-bottom: 16px;
}

.markdown-body :deep(code) {
  font-family: 'Consolas', 'Monaco', 'Menlo', monospace;
  font-size: 13px;
}

.markdown-body :deep(:not(pre) > code) {
  background-color: #f6f8fa;
  padding: 2px 6px;
  border-radius: 4px;
  color: #d63384;
}

.markdown-body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
}

.markdown-body :deep(th),
.markdown-body :deep(td) {
  border: 1px solid #ddd;
  padding: 8px 12px;
  text-align: left;
}

.markdown-body :deep(th) {
  background-color: #f6f8fa;
  font-weight: 600;
}

.markdown-body :deep(tr:nth-child(even)) {
  background-color: #f9f9f9;
}

.markdown-body :deep(blockquote) {
  border-left: 4px solid #1890ff;
  padding: 12px 16px;
  margin: 16px 0;
  background-color: #f6f8fa;
  color: #666;
}

.markdown-body :deep(a) {
  color: #1890ff;
  text-decoration: none;
}

.markdown-body :deep(a:hover) {
  text-decoration: underline;
}

.markdown-body :deep(img) {
  max-width: 100%;
  border-radius: 4px;
}

.markdown-body :deep(hr) {
  border: none;
  border-top: 1px solid #eee;
  margin: 24px 0;
}

/* 响应式 */
@media (max-width: 768px) {
  .doc-wrapper {
    flex-direction: column;
  }
  
  .doc-toc {
    width: 100%;
    order: 2;
  }
  
  .doc-content {
    order: 1;
  }
}
</style>

